import { DefaultButton, DialogFooter, Modal, PrimaryButton } from '@fluentui/react';
import { FC, useCallback } from 'react';
import { MediaTypes } from '../../models/reports.internal';
import { defaultITeamKpiThresholds, ITeamKpiThresholds } from '../../models/reports.thresholds';
import { useGetTeamKpiThresholdsQuery, useUpdateTeamKpiThresholdsMutation } from '../../redux/apis/reportingThresholdsApi';
import { nameof } from '../../utils/stringUtils';
import useFormState from '../../shared/hooks/useFormState';
import useUpdateOnDataReceived from '../../shared/hooks/useUpdateOnDataReceived';
import { ModalHeader } from '../../shared/styled-components';
import BreakdownTimeEditor from './BreakdownTimeEditor';
import PercentageEditor from './PercentageEditor';

interface IProps {
  teamId: string;
  isOpen: boolean;
  onDismiss(): void;
}

const TeamKpiEditModal: FC<IProps> = ({ teamId, isOpen, onDismiss }) => {
  const [thresholds, setThresholds, hasThresholdsChanged, resetThresholds] = useFormState(defaultITeamKpiThresholds);

  const resetTo = useCallback(
    (fT: Partial<ITeamKpiThresholds> | undefined) => resetThresholds({ ...defaultITeamKpiThresholds, ...fT }),
    [resetThresholds],
  );

  const { data: foundThresholds } = useGetTeamKpiThresholdsQuery(teamId, { skip: !isOpen });
  const [updateThresholds] = useUpdateTeamKpiThresholdsMutation();

  useUpdateOnDataReceived(resetTo, foundThresholds);

  const save = useCallback(
    () => {
      if (hasThresholdsChanged)
        updateThresholds({ teamId, thresholds });

      onDismiss();
    },
    [hasThresholdsChanged, onDismiss, teamId, thresholds, updateThresholds],
  );

  const dismiss = useCallback(
    () => {
      resetTo(foundThresholds);
      onDismiss();
    },
    [foundThresholds, onDismiss, resetTo],
  );

  return (
    <Modal isOpen={isOpen} styles={{ main: { width: 375 } }}>
      <ModalHeader title="Edit team thresholds" />
      {
        MediaTypes.map(mt =>
          <div key={mt}>
            <BreakdownTimeEditor label={`${mt} average handling time`}
              value={thresholds.averageHandlingTimeSeconds[mt]}
              onChange={(val) => setThresholds(curr => ({ ...curr, averageHandlingTimeSeconds: { ...curr.averageHandlingTimeSeconds, [mt]: val } }))}
            />
            <PercentageEditor label={`${mt} handled ratio`}
              value={thresholds.handledRatio[mt]}
              onChange={(val) => setThresholds(curr => ({ ...curr, handledRatio: { ...curr.handledRatio, [mt]: val } }))}
            />
            <br />
          </div>,
        )
      }
      <DialogFooter>
        <PrimaryButton onClick={save} disabled={!hasThresholdsChanged}>Save</PrimaryButton>
        <DefaultButton onClick={dismiss}>Cancel</DefaultButton>
      </DialogFooter>
    </Modal>
  );
};

TeamKpiEditModal.displayName = nameof({ TeamKpiEditModal });
export default TeamKpiEditModal;